import './WysiwygCustom.scss';
import React, { useEffect, useRef, useState } from 'react';

// Global promise to prevent duplicate loading of the script
let scriptLoadingPromise = null;

// See Wysiwyg.js for the full explanation — mirrors
// src/styles/font/cmsFont.scss on the frontend under the same stable names.
const FRONTEND_FONT_FACE_CSS = `
@font-face { font-family: "Helvetica Neue"; src: url("https://www.sedarglobal.com/fonts/HelveticaNeue-Regular.otf") format("opentype"); font-display: swap; }
@font-face { font-family: "Helvetica Neue Thin"; src: url("https://www.sedarglobal.com/fonts/HelveticaNeue-Thin.otf") format("opentype"); font-display: swap; }
@font-face { font-family: "Helvetica Neue Light"; src: url("https://www.sedarglobal.com/fonts/HelveticaNeue-Light.otf") format("opentype"); font-display: swap; }
@font-face { font-family: "Helvetica Neue Medium"; src: url("https://www.sedarglobal.com/fonts/HelveticaNeue-Medium.otf") format("opentype"); font-display: swap; }
@font-face { font-family: "Helvetica Neue Bold"; src: url("https://www.sedarglobal.com/fonts/HelveticaNeue-Bold.otf") format("opentype"); font-display: swap; }
body { font-family: "Helvetica Neue", Arial, sans-serif; }
`;
const FRONTEND_FONT_FACE_DATA_URI = 'data:text/css;charset=UTF-8,' + encodeURIComponent(FRONTEND_FONT_FACE_CSS);

function loadCkEditor4Script() {
    if (window.CKEDITOR) {
        return Promise.resolve(window.CKEDITOR);
    }
    if (scriptLoadingPromise) {
        return scriptLoadingPromise;
    }
    scriptLoadingPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js';
        script.onload = () => {
            resolve(window.CKEDITOR);
        };
        script.onerror = (err) => {
            scriptLoadingPromise = null;
            reject(err);
        };
        document.body.appendChild(script);
    });
    return scriptLoadingPromise;
}

function getResolvedImageUrl(originalUrl) {
    if (!originalUrl) return '';
    const serverUrl = process.env.REACT_APP_SERVER_URL;
    const avatarUrl = process.env.REACT_APP_AVATAR_URL;
    if (!avatarUrl) return originalUrl;

    if (!originalUrl.startsWith('http://') && !originalUrl.startsWith('https://')) {
        const cleanAvatarUrl = avatarUrl.endsWith('/') ? avatarUrl : `${avatarUrl}/`;
        const cleanImageUrl = originalUrl.startsWith('/') ? originalUrl.substring(1) : originalUrl;
        return `${cleanAvatarUrl}${cleanImageUrl}`;
    }

    if (serverUrl) {
        const normServerUrl = serverUrl.endsWith('/') ? serverUrl : `${serverUrl}/`;
        const normAvatarUrl = avatarUrl.endsWith('/') ? avatarUrl : `${avatarUrl}/`;
        if (originalUrl.startsWith(normServerUrl)) {
            return originalUrl.replace(normServerUrl, normAvatarUrl);
        }
        const serverUrlNoSlash = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
        const avatarUrlNoSlash = avatarUrl.endsWith('/') ? avatarUrl.slice(0, -1) : avatarUrl;
        if (originalUrl.startsWith(serverUrlNoSlash)) {
            return originalUrl.replace(serverUrlNoSlash, avatarUrlNoSlash);
        }
    }

    return originalUrl;
}

function WysiwygFaq({ onChange, editorLoaded, name, value, disabled = false, lang = 'en' }) {
    const containerRef = useRef(null);
    const editorInstanceRef = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        loadCkEditor4Script()
            .then((CKEDITOR) => {
                if (!containerRef.current) return;

                // Allow all HTML tags, classes, and styles
                CKEDITOR.config.allowedContent = true;
                CKEDITOR.config.extraAllowedContent = 'h1 h2 h3 h4 h5 h6[id,name]; a[!href,id,name,target,rel]; *[id]';
                CKEDITOR.config.versionCheck = false;

                const editor = CKEDITOR.replace(containerRef.current, {
                    allowedContent: true,
                    extraAllowedContent: 'h1 h2 h3 h4 h5 h6[id,name]; a[!href,id,name,target,rel]; *[id]',
                    toolbar: [
                        { name: 'document', items: [ 'Source', '-', 'Maximize', 'ShowBlocks' ] },
                        { name: 'clipboard', items: [ 'Undo', 'Redo' ] },
                        { name: 'editing', items: [ 'SelectAll' ] },
                        { name: 'basicstyles', items: [ 'Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'CopyFormatting', 'RemoveFormat' ] },
                        { name: 'paragraph', items: [ 'NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock' ] },
                        { name: 'links', items: [ 'Link', 'Unlink', 'Anchor' ] },
                        { name: 'insert', items: [ 'Image', 'Table', 'HorizontalRule', 'Smiley', 'SpecialChar' ] },
                        '/',
                        { name: 'styles', items: [ 'Styles', 'Format', 'Font', 'FontSize' ] },
                        { name: 'colors', items: [ 'TextColor', 'BGColor' ] }
                    ],
                    extraPlugins: 'justify,colorbutton,font,copyformatting,showblocks,blockquote,table,smiley,specialchar,horizontalrule,find',
                    // See Wysiwyg.js — "Default/inherit" gives a genuine
                    // default font option instead of the CKEditor font list's
                    // first entry (Arial) being mistaken for one. The
                    // Helvetica Neue entries are the website's own font.
                    font_names: 'Default/inherit;Helvetica Neue/"Helvetica Neue";Helvetica Neue Light/"Helvetica Neue Light";Helvetica Neue Medium/"Helvetica Neue Medium";Helvetica Neue Bold/"Helvetica Neue Bold";Arial/Arial, Helvetica, sans-serif;Comic Sans MS/Comic Sans MS, cursive;Courier New/Courier New, Courier, monospace;Georgia/Georgia, serif;Lucida Sans Unicode/Lucida Sans Unicode, Lucida Grande, sans-serif;Tahoma/Tahoma, Geneva, sans-serif;Times New Roman/Times New Roman, Times, serif;Trebuchet MS/Trebuchet MS, Helvetica, sans-serif;Verdana/Verdana, Geneva, sans-serif',
                    contentsCss: ['https://cdn.ckeditor.com/4.22.1/full-all/contents.css', FRONTEND_FONT_FACE_DATA_URI],
                    versionCheck: false,
                    language: lang,
                    readOnly: disabled,
                    height: 320,
                    removeButtons: ''
                });

                editorInstanceRef.current = editor;

                editor.on('instanceReady', () => {
                    setIsLoaded(true);
                    if (value) {
                        editor.setData(value);
                    }

                    // Overwrite default 'image' command to trigger direct file upload
                    if (editor.commands.image) {
                        editor.commands.image.exec = function() {
                            const fileInput = document.createElement('input');
                            fileInput.type = 'file';
                            fileInput.accept = 'image/*';
                            fileInput.onchange = function(e) {
                                const file = e.target.files[0];
                                if (!file) return;

                                const formData = new FormData();
                                formData.append('upload', file);

                                const url = `${process.env.REACT_APP_SERVER_URL}image-upload`;
                                fetch(url, {
                                    method: 'POST',
                                    body: formData
                                })
                                .then(response => response.json())
                                .then(data => {
                                    if (data.url) {
                                        const resolvedUrl = getResolvedImageUrl(data.url);
                                        const imgHtml = `<img src="${resolvedUrl}" alt="${file.name}" style="max-width: 100%;" />`;
                                        if (editorInstanceRef.current) {
                                            editorInstanceRef.current.insertHtml(imgHtml);
                                        }
                                    } else {
                                        alert(data.error ? (data.error.message || data.error) : 'Upload failed');
                                    }
                                })
                                .catch(error => {
                                    console.error('Upload error:', error);
                                    alert('Upload failed: ' + error.message + ' (URL: ' + url + ')');
                                });
                            };
                            fileInput.click();
                        };
                    }
                });

                // Listen to value changes
                editor.on('change', () => {
                    const data = editor.getData();
                    if (typeof onChange === 'function') {
                        onChange(data);
                    }
                });
            })
            .catch((err) => {
                console.error('Failed to load CKEditor 4:', err);
            });

        return () => {
            if (editorInstanceRef.current) {
                editorInstanceRef.current.destroy(true);
                editorInstanceRef.current = null;
            }
        };
    }, []);

    // Synchronize value changes from parent components
    useEffect(() => {
        const editor = editorInstanceRef.current;
        if (!editor || !isLoaded) return;
        const currentData = editor.getData();
        const targetData = value ?? '';
        if (currentData !== targetData) {
            editor.setData(targetData);
        }
    }, [value, isLoaded]);

    // Synchronize disabled state changes from parent components
    useEffect(() => {
        const editor = editorInstanceRef.current;
        if (!editor || !isLoaded) return;
        editor.setReadOnly(disabled);
    }, [disabled, isLoaded]);

    return (
        <div className='wysiwyg_CKEditor' style={{ minHeight: '340px' }}>
            <textarea ref={containerRef} style={{ display: 'none' }} defaultValue={value ?? ''} />
            {!isLoaded && <div style={{ padding: '10px', color: '#888' }}>Loading Rich Text Editor...</div>}
        </div>
    );
}

export default WysiwygFaq;
